import React, {useContext, useEffect} from 'react'
import {AppbarContentContext} from "../../../routing/appbar/AppbarContentProvider";
import useProfileOverview from "./useProfileOverview";
import Typography from "@mui/material/Typography";
import {Card, CardContent} from "@mui/material";
import {useStyles} from "./ProfileOverviewSection.css";
import {Edit, Https} from "@mui/icons-material";
import AppbarActionButton from "../../../elements/buttons/AppbarActionButton";
import {NavLink} from "react-router-dom";
import moment from "moment";
import LoadingProgressIndication from "../../../elements/progress/loading/LoadingProgressIndication";
import TagContainer from "../../../utils/disciplines/TagContainer";
import AssociationLogo from "../../../utils/logo/AssociationLogo";
import UserProfilePicture from "../../../../admin/sections/users/overview/UserProfilePicture";
import {useIntl} from "react-intl";
import ExperienceIndication from "../../../utils/experience/ExperienceIndication";

const ProfileOverviewSection = () => {
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)
    const {signedInUserData, loading} = useProfileOverview()

    const classes = useStyles()

    // Internationalization for simple strings
    const intl = useIntl();

    const getHeader = () => {
        return signedInUserData.firstName && signedInUserData.secondName
            ? signedInUserData.firstName + ' ' + signedInUserData.secondName
            : signedInUserData.displayName
    }

    const getDescription = () => {
        return signedInUserData.email
    }

    const canPasswordBeChanged = (providers) => {
        let isPassword = false;
        providers.forEach((provider) => {
            if (provider.providerId === 'password') {
                isPassword = true
            }
        })
        return isPassword
    }

    // Setting the appbar content
    useEffect(() => {
        setCurrentAppbarContent({
            header: !loading && getHeader(),
            description: !loading && getDescription(),
            actions: !loading && <>
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.profile.overview.appbar.actions.edit',
                        defaultMessage: 'Edit Profile'
                    })}
                    icon={<Edit/>}
                    component={NavLink}
                    to={'/common/profile/edit'}/>
                {canPasswordBeChanged(signedInUserData.providers) &&
                <AppbarActionButton
                    label={intl.formatMessage({
                        id: 'app.profile.overview.appbar.actions.changePassword',
                        defaultMessage: 'Change Password'
                    })}
                    icon={<Https/>}
                    component={NavLink}
                    to='/common/profile/changePassword'/>}
            </>
        })

    }, [signedInUserData, loading])

    const showOrGotoEdit = (condition, element) => {
        if (!condition) {
            return <NavLink to={'/common/profile/edit'}>Complete your data</NavLink>
        }
        return element
    }

    if (loading) {
        return <LoadingProgressIndication text={intl.formatMessage({
            id: 'app.profile.overview.loading',
            defaultMessage: 'Retrieving profile data...'
        })}/>
    }

    return (
        <div>

            {/* Standard info about the profile */}
            <Card className={classes.card}>
                <div className={classes.cardIcon}>
                    <UserProfilePicture user={signedInUserData} height={80} width={80}/>
                </div>
                <CardContent>
                    <Typography variant="h5" component="h2">
                        {intl.formatMessage({
                            id: 'app.profile.overview.details.info.header',
                            defaultMessage: 'Profile Info'
                        })}
                    </Typography>

                    <div className={classes.line}>
                            <span>
                                <b>{intl.formatMessage({
                                    id: 'app.profile.overview.details.info.fullName.label',
                                    defaultMessage: 'Full Name'
                                })}: </b>
                                {showOrGotoEdit(signedInUserData.firstName && signedInUserData.secondName,
                                    signedInUserData.firstName + ' ' + signedInUserData.secondName)}
                            </span>
                    </div>
                    <div className={classes.line}>
                            <span><b>{intl.formatMessage({
                                id: 'app.profile.overview.details.info.email.label',
                                defaultMessage: 'Email'
                            })}:</b> {signedInUserData.email}</span>
                    </div>
                    <div className={classes.line}>
                            <span><b>{intl.formatMessage({
                                id: 'app.profile.overview.details.info.lastSignIn.label',
                                defaultMessage: 'Last Sign-In'
                            })}:</b> {moment(signedInUserData.lastSignIn).format('DD.MM.YYYY (HH:mm:ss)')}</span>
                    </div>
                    <div className={classes.line}>
                            <span><b>{intl.formatMessage({
                                id: 'app.profile.overview.details.info.role.label',
                                defaultMessage: 'Role'
                            })}:</b> {intl.formatMessage({
                                id: `app.ids.role.${signedInUserData.role}`,
                                defaultMessage: 'signedInUserData.role'
                            })}</span>
                    </div>
                    <div className={classes.line}>
                        <div className={classes.row}>
                            <b>{intl.formatMessage({
                                id: 'app.profile.overview.details.info.scopes.label',
                                defaultMessage: 'Scopes'
                            })}: </b>
                            {signedInUserData?.roleScopes?.map((association) => (
                                <AssociationLogo key={association} association={association} height={30} width={30}/>
                            ))}
                        </div>
                    </div>
                </CardContent>
            </Card>


            {/* Profile info with respect to the assigned associations (disciplines and experience in association) */}
            <div>
                {signedInUserData?.associationInfo && Object.keys(signedInUserData?.associationInfo).map((association) => (
                    <Card key={association} className={classes.card}>

                        {/* Association logo */}
                        <div className={classes.cardIcon}>
                            <AssociationLogo key={association} association={association} height={80} width={80}/>
                        </div>

                        {/* Info about disciplines and experience */}
                        <CardContent>
                            <div className={classes.row}>
                                <div style={{marginRight: 10}}>
                                    {showOrGotoEdit(signedInUserData.associationInfo[association]?.experience,
                                        <ExperienceIndication
                                            experience={signedInUserData.associationInfo[association].experience}/>)}
                                </div>

                                <div>
                                    {showOrGotoEdit(
                                        signedInUserData.associationInfo[association]?.disciplines?.length > 0,
                                        <TagContainer
                                            tags={signedInUserData.associationInfo[association].disciplines?.map((discipline) =>
                                                intl.formatMessage({id: `app.ids.discipline.${discipline}`}))}/>)}
                                </div>
                            </div>
                        </CardContent>
                    </Card>


                ))}
            </div>
        </div>
    )
}

export default ProfileOverviewSection
